import { useState } from "react";
import { Alert, Box, Button, Grid, Paper, Stack, TextField, Typography } from "@mui/material";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import VisibilityOutlinedIcon from "@mui/icons-material/VisibilityOutlined";
import PaletteOutlinedIcon from "@mui/icons-material/PaletteOutlined";
import CommercialOfferPreview from "./CommercialOfferPreview";
import AppDatePicker from "../../components/common/AppDatePicker";
import CommercialProposalFields from "./CommercialProposalFields";
import { COMMERCIAL_OFFER_LIMITS, COMMERCIAL_OFFER_SECTION_ID, normalizeCommercialOffer, isProjectCommercialMode } from "../../components/config/commercialOffer";
import { assetTypeDefinition } from "../../utils/projectAssets";

const PREVIEW_OPEN_KEY = "pythia.offerPreview.open";
function readPreviewOpen() {
  try {
    return window.localStorage.getItem(PREVIEW_OPEN_KEY) === "true";
  } catch {
    return false;
  }
}

export default function CommercialOfferFields({ value, onChange, isGreek, demoMode, systemType, reportLanguage, regulatoryMode, logoSrc, projectId, sx }) {
  const [previewOpen, setPreviewOpen] = useState(readPreviewOpen);
  const togglePreview = (next) => {
    setPreviewOpen(next);
    try {
      window.localStorage.setItem(PREVIEW_OPEN_KEY, String(next));
    } catch {
      // storage unavailable: the choice lasts for this visit only
    }
  };
  const terms = normalizeCommercialOffer(value, { systemType, reportLanguage, regulatoryMode });
  const asset = assetTypeDefinition(systemType);
  const canOfferPvOnly = !isProjectCommercialMode(regulatoryMode) && Boolean(asset?.hasPv && asset?.hasBess);
  const tr = (el, en) => isGreek ? el : en;
  const changeTerms = (next) => onChange(normalizeCommercialOffer(next, { systemType, reportLanguage, regulatoryMode }));
  const update = (key, next) => changeTerms({ ...terms, [key]: next });
  const fields = [
    ["reference", tr("Αριθμός προσφοράς", "Offer reference")],
    ["recipient", tr("Παραλήπτης (προαιρετικό)", "Recipient (optional)")],
    ["scope", tr("Περιλαμβανόμενες εργασίες / εξοπλισμός", "Included works / equipment")],
    ["payment", tr("Τρόπος πληρωμής", "Payment terms")],
    ["notes", tr("Διευκρινίσεις / εξαιρέσεις", "Clarifications / exclusions")],
  ];
  const textField = ([key, label]) => (
    <TextField fullWidth label={label} value={terms[key]} disabled={demoMode}
      inputProps={{ maxLength: COMMERCIAL_OFFER_LIMITS[key] }}
      InputLabelProps={{ shrink: true }}
      multiline={COMMERCIAL_OFFER_LIMITS[key] === 220}
      minRows={COMMERCIAL_OFFER_LIMITS[key] === 220 ? 2 : undefined}
      helperText={`${terms[key].length}/${COMMERCIAL_OFFER_LIMITS[key]}${key === "scope" ? tr(" · Για λίστα με τικ στην αναλυτική προσφορά: μία εργασία ανά γραμμή, όχι κόμματα.", " · For a checklist in the detailed proposal: one item per line, not commas.") : ""}`}
      onChange={(e) => update(key, e.target.value)} />
  );
  return (
    <Paper id={COMMERCIAL_OFFER_SECTION_ID} tabIndex={-1} elevation={2}
      sx={[{ p: 2.5, borderRadius: 3, scrollMarginTop: 120 }, sx]}>
      <Stack spacing={2}>
        <Stack direction="row" spacing={1} alignItems="center">
          <DescriptionOutlinedIcon color="primary" fontSize="small" />
          <Typography variant="subtitle1" component="h2" sx={{ fontWeight: 700, flex: 1 }}>
            {tr("Στοιχεία οικονομικής προσφοράς", "Commercial offer details")}
          </Typography>
          {!previewOpen ? (
            <Button size="small" variant="outlined" startIcon={<VisibilityOutlinedIcon />} onClick={() => togglePreview(true)}>
              {tr("Προεπισκόπηση PDF", "PDF preview")}
            </Button>
          ) : null}
        </Stack>
        <Box sx={{ display: "grid", gap: 2, alignItems: "start",
          gridTemplateColumns: { xs: "minmax(0, 1fr)", lg: previewOpen ? "minmax(0, 1fr) minmax(0, 1fr)" : "minmax(0, 1fr)" } }}>
        {previewOpen ? (
          <Box sx={{ order: { xs: 0, lg: 2 }, position: { lg: "sticky" }, top: { lg: 72 } }}>
            <CommercialOfferPreview projectId={projectId} terms={terms} isGreek={isGreek} regulatoryMode={regulatoryMode}
              onClose={() => togglePreview(false)}
              onProposalChange={demoMode ? undefined : (next) => update("proposal", { ...terms.proposal, ...next })}
              onPricingChange={demoMode ? undefined : (next) => update("pricing", { ...terms.pricing, ...next })} />
          </Box>
        ) : null}
        <Stack spacing={2} sx={{ order: 1, minWidth: 0 }}>
        {!demoMode ? (
          <Alert severity="info" icon={<PaletteOutlinedIcon fontSize="inherit" />}
            action={!previewOpen ? (
              <Button color="inherit" size="small" onClick={() => togglePreview(true)}>{tr("Άνοιγμα", "Open")}</Button>
            ) : null}>
            {previewOpen
              ? tr("Χρώμα, γραμματοσειρά και σελίδες της προσφοράς: από τη γραμμή «Εμφάνιση» της προεπισκόπησης.",
                "Offer colour, typeface and pages: from the ‘Appearance’ row of the preview.")
              : tr("Χρώμα, γραμματοσειρά και σελίδες της προσφοράς ρυθμίζονται από την «Προεπισκόπηση PDF», όπου βλέπετε αμέσως το αποτέλεσμα.",
                "Offer colour, typeface and pages are set in the ‘PDF preview’, where you see the result at once.")}
          </Alert>
        ) : null}
        <Typography variant="body2" color="text.secondary">
          {tr("Τα στοιχεία προσφοράς δεν επηρεάζουν τους υπολογισμούς της μελέτης. Αποθηκεύονται μαζί με τη συγκεκριμένη μονάδα του έργου με την «Αποθήκευση» και οι αλλαγές τους εμφανίζονται στην επόμενη λήψη προσφοράς, χωρίς νέα προσομοίωση. Η παρουσίαση και τα στοιχεία εταιρείας χρησιμοποιούνται επίσης στην τελευταία σελίδα της αναλυτικής αναφοράς όταν εκδίδεται η μελέτη.",
            "Offer details do not affect the study calculations. Use Save to keep them with this asset; changes appear in the next offer download without another simulation. The company profile and contact details are also used on the full report's final page when the study is generated.")}
        </Typography>
        <Alert severity={demoMode ? "warning" : "info"}>
          {demoMode
            ? tr("Στις αναφορές επίδειξης δεν χρησιμοποιούνται εξατομικευμένα στοιχεία προσφοράς. Το PDF εξάγεται με ουδέτερο παραλήπτη και προεπιλεγμένους όρους. Τυχόν αποθηκευμένα στοιχεία διατηρούνται, αλλά δεν εμφανίζονται στην εξαγωγή επίδειξης.",
              "Illustrative reports do not use personalised offer details. The PDF uses a neutral recipient and default terms. Previously saved details are retained, but are not shown in the illustrative export.")
            : tr("Τα αποτελέσματα, το κόστος επένδυσης, το λογότυπο και η γλώσσα προέρχονται από την τελευταία επιτυχημένη μελέτη. Εδώ ρυθμίζετε τον ΦΠΑ και τα στοιχεία της προσφοράς. Τα κενά πεδία χρησιμοποιούν ουδέτερους όρους και τον πελάτη της μελέτης.",
              "Results, investment cost, logo and language come from the latest successful study. Set VAT and offer details here. Blank fields use neutral terms and the study's client.")}
        </Alert>
        <Grid container spacing={2}>
          {fields.slice(0, 2).map((field) => (
            <Grid size={{ xs: 12, md: 6 }} key={field[0]}>{textField(field)}</Grid>
          ))}
          <Grid size={{ xs: 12, md: 6 }}>
            <AppDatePicker fullWidth size="medium" disabled={demoMode}
              label={tr("Ισχύς προσφοράς έως (προαιρετικό)", "Offer valid until (optional)")}
              value={terms.valid_until} onChange={(next) => update("valid_until", next)} />
          </Grid>
          {fields.slice(2).map((field) => (
            <Grid size={12} key={field[0]}>{textField(field)}</Grid>
          ))}
        </Grid>
        <Typography variant="caption" color="text.secondary">
          {tr("Ο ΦΠΑ εγκατάστασης έχει προεπιλογή 24% και είναι ανεξάρτητος από τον ΦΠΑ του τιμολογίου ηλεκτρικής ενέργειας. Επιβεβαιώστε τιμές, έκταση εργασιών και φορολογική μεταχείριση πριν αποστείλετε την προσφορά.",
            "Installation VAT defaults to 24% and is independent of electricity-tariff VAT. Confirm prices, scope and tax treatment before sending the offer.")}
        </Typography>
        <CommercialProposalFields terms={terms} onChange={changeTerms} isGreek={isGreek} disabled={demoMode} canOfferPvOnly={canOfferPvOnly} regulatoryMode={regulatoryMode} logoSrc={logoSrc} reportLanguage={reportLanguage} />
        </Stack>
        </Box>
      </Stack>
    </Paper>
  );
}
